import { useQuery } from "@tanstack/react-query";
import type { SellerCustomerListResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSellerCustomersQueryParams = {
  storeSlug: string;
  search?: string;
  limit?: number;
  offset?: number;
};

function buildPath(params: Omit<UseSellerCustomersQueryParams, "storeSlug">) {
  const query = new URLSearchParams();
  if (params.search) {
    query.set("search", params.search);
  }
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.seller.customers}?${queryString}`
    : endpoints.seller.customers;
}

export function useSellerCustomersQuery(params: UseSellerCustomersQueryParams) {
  const { storeSlug, ...rest } = params;
  const query = useQuery({
    queryKey: queryKeys.seller.customers(storeSlug, {
      search: rest.search,
      limit: rest.limit,
      offset: rest.offset,
    }),
    queryFn: () =>
      apiJsonClient<SellerCustomerListResponse>(buildPath(rest)),
    enabled: storeSlug.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
